<template>
  <div>
    <p v-if="isLoading" class="px-3 py-2 text-sm text-slate-500">Hämtar fakturor...</p>
    <p v-else-if="!invoices.length" class="px-3 py-2 text-sm text-slate-500">Inga fakturor.</p>

    <AppTable v-else expandable>
      <thead>
        <tr>
          <th>Förfallodag</th>
          <th>Kvarvarande belopp</th>
          <th>Status</th>
          <th />
        </tr>
      </thead>
      <tbody>
        <template v-for="invoice in invoices" :key="invoice.id">
          <AppTableRow class="cursor-pointer" @click="void toggleExpand(invoice.id)">
            <AppTableCell>{{ formatDateIfValid(invoice.dueDate) }}</AppTableCell>
            <AppTableCell nowrap>{{ formatAmount(invoice.amounts.remaining.value) }} kr</AppTableCell>
            <AppTableCell>
              <span class="inline-flex items-center gap-2">
                {{ statusPresentation(invoice).label }}
                <SvgIcon
                  v-if="statusPresentation(invoice).icon"
                  :class="statusPresentation(invoice).icon!.className"
                  :path="statusPresentation(invoice).icon!.path"
                  size="18"
                />
              </span>
            </AppTableCell>
            <AppTableCell>
              <SvgIcon
                class="ml-auto block"
                :path="expandedIds.has(invoice.id) ? mdiChevronUp : mdiChevronDown"
                size="18"
              />
            </AppTableCell>
          </AppTableRow>

          <AppTableRow v-if="expandedIds.has(invoice.id)" :key="invoice.id + '-detail'" expanded-content>
            <AppTableCell colspan="4">
              <p v-if="detailLoading.has(invoice.id)" class="text-sm text-slate-500">Hämtar fakturauppgifter...</p>
              <InvoiceDetailCard v-else-if="details.get(invoice.id)" :detail="details.get(invoice.id)!" />
            </AppTableCell>
          </AppTableRow>
        </template>
      </tbody>
    </AppTable>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import { AppTable, AppTableRow, AppTableCell, SvgIcon } from '@staff/ui'
import { formatAmount, formatDateIfValid } from '@staff/shared'
import type { InvoiceDetail, InvoiceListItem } from '@/services/invoice'
import InvoiceDetailCard from './InvoiceDetailCard.vue'
import { getInvoicePaymentStatus, invoiceStatusPresentation } from './invoiceStatus'
import { useInvoices } from '@/composables/useInvoices'
import { mdiChevronUp, mdiChevronDown } from '@mdi/js'

const props = defineProps<{
  userId: string
}>()

const userId = computed(() => props.userId)
const { isLoading, invoices, fetchDetail } = useInvoices(userId)

const expandedIds = ref<Set<string>>(new Set())
const detailLoading = ref<Set<string>>(new Set())
const details = ref<Map<string, InvoiceDetail>>(new Map())

const statusPresentation = (invoice: InvoiceListItem) => invoiceStatusPresentation[getInvoicePaymentStatus(invoice)]

const toggleExpand = async (id: string) => {
  if (expandedIds.value.has(id)) {
    expandedIds.value.delete(id)
    return
  }

  expandedIds.value.add(id)

  if (!details.value.has(id)) {
    detailLoading.value.add(id)
    const result = await fetchDetail(id)
    if (result) details.value.set(id, result)
    detailLoading.value.delete(id)
  }
}
</script>

<style scoped lang="scss">
.status-paid {
  color: var(--color-icon-success);
}

.status-overdue {
  color: var(--color-icon-warning);
}
</style>
